/**
 * relatedness — ranks articles by how closely their topic matches a target.
 *
 * Used for the blog's "What to read next" shelf and for the Related Reading
 * pool the publish cron hands the writer (previously the first 30 live rows in
 * plan order, whatever their topic). Scoring is the same idea as clusterMatch:
 * shared distinctive terms count for more than common ones ("seo" is in nearly
 * every title, so it carries almost no weight), plus a bonus per shared
 * cluster/category. Ties go to the newer article.
 */
import { tokenize } from "./clusterMatch.js";

export interface RelatedCandidate<T> {
  item: T;
  /** Title, keyword, tags — whatever describes the topic. */
  text: string;
  /** Clusters or categories; each one shared with the target adds a bonus. */
  groups?: string[];
  /** ISO or M/D/YYYY date for tie-breaks; newer first. */
  date?: string;
}

const GROUP_BONUS = 1.5;

export function rankByRelevance<T>(
  target: { text: string; groups?: string[] },
  candidates: RelatedCandidate<T>[],
  limit: number,
): T[] {
  const docs = candidates.map(c => new Set(tokenize(c.text)));
  const df = new Map<string, number>();
  for (const d of docs) for (const t of Array.from(d)) df.set(t, (df.get(t) ?? 0) + 1);
  const idf = (t: string) => Math.log((candidates.length + 1) / ((df.get(t) ?? 0) + 1));

  const targetTokens = new Set(tokenize(target.text));
  const targetGroups = new Set(target.groups ?? []);
  const time = (d?: string) => (d ? Date.parse(d) || 0 : 0);

  return candidates
    .map((c, i) => {
      let score = 0;
      for (const t of Array.from(targetTokens)) if (docs[i].has(t)) score += idf(t);
      for (const g of c.groups ?? []) if (targetGroups.has(g)) score += GROUP_BONUS;
      return { c, score };
    })
    .sort((a, b) => b.score - a.score || time(b.c.date) - time(a.c.date))
    .slice(0, limit)
    .map(r => r.c.item);
}
